import { Fragment } from "react/jsx-runtime";
import classNames from "classnames/bind";
import { ProductCard } from "../ProductCard";
import { ConfigProps, ProductListLayout } from "../../types/types";
import styles from "./Grid.module.scss";
import { CreditBanner } from "../../product-list-components/CreditBanner/CreditBanner";
import { YouveViewed } from "../../product-list-components/YouveViewed/YouveViewed";
import { useFeatureFlags } from "@~/flags/flags";
import { DisruptorBlock } from "@~/product-list-components/DisruptorBlock/DisruptorBlock";
import { getProductKey } from "../utils/getProductKey";
import { getCreditBannerDetails } from "../utils/getCreditBannerDetails";
import { ColumnStyles } from "../shared/ColumnStyles";

const cx = classNames.bind(styles);

const getContainerTestId = (
  configProps: ConfigProps,
  largeCardsEnabled: boolean,
) => {
  if (configProps?.fullWidth) {
    return "grid-container-fullWidth";
  }
  if (configProps?.listView) {
    return "grid-container-list-view";
  }
  if (largeCardsEnabled) {
    return "grid-container-large-cards";
  }
  return "grid-container-small-cards";
};

export const Grid: ProductListLayout = ({
  configProps,
  config,
  products,
  resultsInfo,
  instanceId,
  consumerId,
}) => {
  const flags = useFeatureFlags();
  const youveViewedEnabled = configProps?.youveViewed ?? true;
  const creditBannersEnabled = configProps?.creditBanner ?? true;
  const disruptorBlocksEnabled = configProps?.disruptorBlock ?? true;
  const largeCardsEnabled = Boolean(flags.largeCards?.value);

  const creditBannerDetails = getCreditBannerDetails(
    products,
    resultsInfo.chunkSize,
  );

  return (
    <>
      <ColumnStyles
        columnsByBreakpoint={configProps?.columnsByBreakpoint}
        className={cx("grid-container")}
      />
      <section
        className={cx("grid-container")}
        data-testid={getContainerTestId(configProps, largeCardsEnabled)}
      >
        {products.map((product, ind) => {
          const hasCreditBanner =
            creditBannersEnabled && creditBannerDetails.indices.includes(ind);

          return (
            <Fragment key={getProductKey(product)}>
              <ProductCard
                product={product}
                config={config}
                index={ind}
                instanceId={instanceId}
                consumerId={consumerId}
              />

              {disruptorBlocksEnabled && (
                <DisruptorBlock
                  flags={flags}
                  indexInGrid={ind}
                  productCount={products.length}
                  finalCreditBannerIndex={
                    creditBannerDetails.indices[
                      creditBannerDetails.indices.length - 1
                    ] || 0
                  }
                />
              )}

              {hasCreditBanner && (
                <CreditBanner
                  creditBannerType={creditBannerDetails.type}
                  className={styles.fullWidth}
                />
              )}

              {youveViewedEnabled && (
                <YouveViewed
                  resultsInfo={resultsInfo}
                  numProductsOnPageAbove={ind + 1}
                />
              )}
            </Fragment>
          );
        })}
      </section>
    </>
  );
};
